import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Trophy, Coffee, Layers, Target, Award, Crown, Star, MessageCircle, Sunrise, Lock, Sparkles } from "lucide-react";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";

const ICON_MAP = {
  Coffee, Layers, Target, Trophy, Award, Crown, Star, MessageCircle, Sunrise,
};
const TIER_STYLES = {
  bronze: { bg: "from-amber-700 to-amber-900", ring: "ring-amber-700/30", label: "Bronze", label_ar: "برونزي" },
  silver: { bg: "from-stone-400 to-stone-600", ring: "ring-stone-500/30", label: "Silver", label_ar: "فضي" },
  gold: { bg: "from-amber-400 to-amber-600", ring: "ring-amber-500/40", label: "Gold", label_ar: "ذهبي" },
};

export default function Achievements() {
  const { lang, isRTL } = useI18n();
  const { user } = useAuth();
  const serifClass = isRTL ? "font-serif-ar" : "font-serif-en";

  const { data, isLoading } = useQuery({
    queryKey: ["my-achievements"],
    queryFn: endpoints.myAchievements,
    enabled: !!user,
    refetchInterval: 30000,
  });

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-20 pb-24 text-center" data-testid="achievements-page-guest">
        <Trophy className="w-12 h-12 mx-auto text-[#C86240]/40 mb-3" />
        <h1 className={`${serifClass} text-4xl text-[#2A2421] mb-3`}>
          {isRTL ? "شاراتك تنتظرك" : "Your badges await"}
        </h1>
        <p className="text-[#5C544F] mb-6">
          {isRTL ? "سجّل دخولك لبدء جمع الشارات." : "Sign in to start collecting badges."}
        </p>
        <Link to="/login">
          <Button className="rounded-full bg-[#2A2421] hover:bg-[#2A2421]/90 text-[#F9F6F0]" data-testid="achievements-signin-btn">
            {isRTL ? "سجّل الدخول" : "Sign in"}
          </Button>
        </Link>
      </div>
    );
  }

  const items = data?.items || [];
  const unlocked = items.filter((i) => i.unlocked);
  const locked = items.filter((i) => !i.unlocked);
  const stats = data?.stats || {};
  const progress = data?.total ? Math.round((data.unlocked_count / data.total) * 100) : 0;

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-24" data-testid="achievements-page">
      <div className="max-w-3xl">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C86240] mb-4 flex items-center gap-2">
          <Sparkles className="w-4 h-4" /> {isRTL ? "إنجازاتك" : "Your achievements"}
        </div>
        <h1 className={`${serifClass} text-4xl lg:text-6xl text-[#2A2421] leading-[1.02]`}>
          {isRTL ? "شارات تشهد على مهارتك" : "Badges that prove your craft"}
        </h1>
        <p className="mt-5 text-lg text-[#5C544F] leading-relaxed">
          {isRTL
            ? "كل تخميرة، تقييم، أو تحدي تكسبك شارة جديدة. اجمع كل الشارات لتصبح أسطورة التخمير."
            : "Every brew, rating, or challenge earns you a new badge. Collect them all to become a brewing legend."}
        </p>
      </div>

      {/* Progress strip */}
      <div className="mt-10 soft-card rounded-2xl p-6 flex items-center gap-5 flex-wrap" data-testid="achievements-progress">
        <div className="relative w-20 h-20 shrink-0">
          <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90">
            <circle cx="18" cy="18" r="16" fill="none" stroke="#E8E2D8" strokeWidth="2.5" />
            <circle cx="18" cy="18" r="16" fill="none" stroke="#C86240" strokeWidth="2.5"
              strokeDasharray={`${progress} 100`} strokeLinecap="round" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center font-bold text-[#2A2421]">{progress}%</div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className={`${serifClass} text-2xl text-[#2A2421]`}>
            {data?.unlocked_count}/{data?.total} {isRTL ? "شارة مفتوحة" : "badges unlocked"}
          </div>
          <div className="text-xs text-[#5C544F] mt-1">
            {isRTL
              ? `${stats.brew_count || 0} تخميرة · ${stats.challenge_subs || 0} مشاركة تحدّي · ${stats.ratings || 0} تقييم`
              : `${stats.brew_count || 0} brews · ${stats.challenge_subs || 0} challenge subs · ${stats.ratings || 0} ratings`}
          </div>
        </div>
        <Link to="/calculator?tab=extraction">
          <Button variant="outline" className="rounded-full border-[#C86240]/40 text-[#C86240] hover:bg-[#C86240]/10" data-testid="achievements-cta-brew">
            <Coffee className="w-4 h-4 me-2" />
            {isRTL ? "خمّر الآن" : "Brew now"}
          </Button>
        </Link>
      </div>

      {/* Unlocked */}
      {unlocked.length > 0 && (
        <section className="mt-12">
          <h2 className={`${serifClass} text-2xl text-[#2A2421] mb-5`}>
            {isRTL ? "مفتوحة" : "Unlocked"} ({unlocked.length})
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {unlocked.map((a) => <BadgeCard key={a.key} a={a} unlocked />)}
          </div>
        </section>
      )}

      {/* Locked */}
      {locked.length > 0 && (
        <section className="mt-12">
          <h2 className={`${serifClass} text-2xl text-[#2A2421] mb-5 opacity-70`}>
            {isRTL ? "مغلقة" : "Locked"} ({locked.length})
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {locked.map((a) => <BadgeCard key={a.key} a={a} unlocked={false} />)}
          </div>
        </section>
      )}

      {isLoading && unlocked.length === 0 && locked.length === 0 && (
        <div className="mt-10 text-center text-sm text-[#5C544F]">…</div>
      )}
    </div>
  );
}

function BadgeCard({ a, unlocked }) {
  const { lang, isRTL } = useI18n();
  const isMystery = a.secret && !unlocked;
  const Icon = isMystery ? Sparkles : (ICON_MAP[a.icon] || Trophy);
  const tier = TIER_STYLES[a.tier] || TIER_STYLES.bronze;
  const title = isMystery ? (isRTL ? "شارة سرية" : "Mystery Badge") : (lang === "ar" ? a.title_ar : a.title_en);
  const desc = isMystery ? (isRTL ? "اكتشف كيف تفتحها…" : "Discover how to unlock it…") : (lang === "ar" ? a.desc_ar : a.desc_en);
  return (
    <div
      className={`relative soft-card rounded-2xl p-5 text-center transition-all ${unlocked ? "hover:-translate-y-1" : "opacity-60 grayscale"}`}
      data-testid={`achievement-${a.key}`}
    >
      {a.secret && unlocked && (
        <div className="absolute top-2 end-2 text-[9px] uppercase tracking-widest text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full" data-testid={`achievement-${a.key}-secret-badge`}>
          {isRTL ? "سرّية" : "Secret"}
        </div>
      )}
      <div className={`relative mx-auto w-16 h-16 mb-3 rounded-2xl bg-gradient-to-br ${tier.bg} flex items-center justify-center ring-4 ${tier.ring} shadow-md`}>
        <Icon className="w-7 h-7 text-white" />
        {!unlocked && (
          <div className="absolute inset-0 rounded-2xl bg-black/40 flex items-center justify-center">
            <Lock className="w-5 h-5 text-white" />
          </div>
        )}
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C544F] mb-1">
        {isRTL ? tier.label_ar : tier.label}
      </div>
      <div className="font-semibold text-[#2A2421] mb-1 leading-tight">
        {title}
      </div>
      <div className="text-xs text-[#5C544F] leading-snug">
        {desc}
      </div>
      {unlocked && a.unlocked_at && (
        <div className="text-[9px] text-[#C86240] mt-2 uppercase tracking-widest">
          {new Date(a.unlocked_at).toLocaleDateString()}
        </div>
      )}
    </div>
  );
}
